import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { DollarSign, Loader2, Calculator, Package, HardHat, FileSpreadsheet, FileText } from "lucide-react";
import { api } from "../lib/apiClient";
import jsPDF from "jspdf";
import { toast } from "sonner";

const TIERS = ["Economy", "Standard", "Premium"];
const fmt = (n) => "$" + (n || 0).toLocaleString();

export default function CostEstimator({ open, onOpenChange, blueprint, stories = 1, site, floors, lot, defaultTier = "Standard" }) {
  const [tier, setTier] = useState(defaultTier);
  const [region, setRegion] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) setTier(defaultTier); }, [open, defaultTier]);

  const estimate = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await api.post("/estimate/cost", {
        blueprint, tier, region_multiplier: Number(region), stories,
        slope_type: site?.slope_type || "Flat", slope_pct: site?.slope_pct || 0,
        floors: floors && floors.length ? floors : null, lot: lot || null,
      });
      setResult(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Estimate failed"); }
    finally { setLoading(false); }
  };

  const matByCat = (result?.materials || []).reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m); return acc;
  }, {});

  const downloadCsv = () => {
    const mats = result?.materials || [];
    if (!mats.length) return;
    const head = ["Category", "Material", "Qty", "Unit", "Unit Cost", "Cost"];
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const rows = mats.map((m) => [m.category, m.item, m.qty, m.unit, m.unit_cost, m.cost].map(esc).join(","));
    rows.push(["", "", "", "", "TOTAL", result.materials_total].map(esc).join(","));
    const csv = [head.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "bill-of-materials.csv"; a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Materials CSV downloaded");
  };

  const downloadBomPdf = () => {
    const mats = result?.materials || [];
    if (!mats.length) return;
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    pdf.setFontSize(16); pdf.text("Bill of Materials", 40, 48);
    pdf.setFontSize(9); pdf.setTextColor(120);
    pdf.text(`Tier ${result.tier} · Auto takeoff · Materials total $${(result.materials_total || 0).toLocaleString()}`, 40, 66);
    pdf.setTextColor(0);
    let y = 92;
    const nl = (need) => { if (y > ph - need) { pdf.addPage(); y = 48; } };
    Object.entries(matByCat).forEach(([cat, items]) => {
      nl(60);
      pdf.setFontSize(11); pdf.setTextColor(20, 100, 200); pdf.text(cat, 40, y); y += 14;
      pdf.setFontSize(8); pdf.setTextColor(120);
      pdf.text("MATERIAL", 48, y); pdf.text("QTY", 300, y); pdf.text("UNIT $", 380, y); pdf.text("COST", 470, y);
      y += 4; pdf.setDrawColor(210); pdf.line(40, y, pw - 40, y); y += 12;
      items.forEach((m) => {
        nl(30);
        pdf.setFontSize(9); pdf.setTextColor(0); pdf.text(String(m.item), 48, y);
        pdf.setTextColor(90);
        pdf.text(`${m.qty.toLocaleString()} ${m.unit}`, 300, y);
        pdf.text(`$${m.unit_cost.toLocaleString()}`, 380, y);
        pdf.setTextColor(0); pdf.text(`$${m.cost.toLocaleString()}`, 470, y);
        y += 14;
      });
      y += 12;
    });
    nl(30); pdf.setDrawColor(180); pdf.line(40, y - 4, pw - 40, y - 4);
    pdf.setFontSize(11); pdf.setTextColor(20, 100, 200);
    pdf.text(`Materials total: $${(result.materials_total || 0).toLocaleString()}`, 40, y + 12);
    pdf.save("bill-of-materials.pdf");
    toast.success("Materials PDF downloaded");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#161B22] border-[#30363D] text-[#F0F6FC] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <Calculator className="w-5 h-5 text-[#34D399]" /> Build Cost & Materials
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">A construction budget and full material takeoff from your plan — including foundation/basement.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs text-[#8B949E]">Quality Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger data-testid="cost-tier-select" className="bg-[#0D1117] border-[#30363D]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                {TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#8B949E]">Region Multiplier</Label>
            <Input type="number" step="0.05" min="0.1" value={region} onChange={(e) => setRegion(e.target.value)} className="bg-[#0D1117] border-[#30363D]" data-testid="cost-region-input" />
          </div>
          <Button data-testid="cost-estimate-btn" onClick={estimate} disabled={loading} className="bg-[#34D399] hover:bg-[#10B981] text-[#0D1117] font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-1" /> Estimate</>}
          </Button>
        </div>

        {result && (
          <Tabs defaultValue="cost" className="pt-2 animate-fade-up" data-testid="cost-results">
            <TabsList className="bg-[#0D1117] border border-[#30363D]">
              <TabsTrigger value="cost" data-testid="tab-cost"><HardHat className="w-3.5 h-3.5 mr-1.5" /> Cost Breakdown</TabsTrigger>
              <TabsTrigger value="materials" data-testid="tab-materials"><Package className="w-3.5 h-3.5 mr-1.5" /> Materials ({result.materials?.length || 0})</TabsTrigger>
            </TabsList>

            <TabsContent value="cost" className="space-y-4 pt-3">
              <div className="rounded-xl border border-[#30363D] bg-gradient-to-br from-[#0D1117] to-[#161B22] p-5 flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8B949E] font-mono-cad">Estimated Total</div>
                  <div className="font-heading text-3xl font-bold text-[#34D399]" data-testid="cost-total">{fmt(result.total)}</div>
                </div>
                <div className="text-right text-xs font-mono-cad text-[#8B949E]">
                  <div>{fmt(result.cost_per_sqft)}/ft²</div>
                  <div>{result.total_area?.toLocaleString()} ft² · {result.tier}</div>
                </div>
              </div>

              {result.foundation?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8B949E] font-mono-cad mb-2">Foundation / Below-Grade</div>
                  <div className="rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/5 overflow-hidden" data-testid="cost-foundation">
                    {result.foundation.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm border-b border-[#21262D] last:border-0">
                        <span className="text-[#F59E0B]">{r.label}</span>
                        <span className="text-[#8B949E] font-mono-cad text-xs">{r.detail}</span>
                        <span className="font-mono-cad text-[#F0F6FC]">{fmt(r.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-widest text-[#8B949E] font-mono-cad mb-2">Per Room</div>
                <div className="rounded-lg border border-[#30363D] overflow-hidden">
                  {result.rooms.map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm border-b border-[#21262D] last:border-0">
                      <span className="truncate max-w-[55%]">{r.name}</span>
                      <span className="text-[#8B949E] font-mono-cad text-xs">{r.area} ft² × {fmt(r.rate)}</span>
                      <span className="font-mono-cad text-[#F0F6FC]">{fmt(r.cost)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {result.disciplines.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8B949E] font-mono-cad mb-2">Systems</div>
                  <div className="rounded-lg border border-[#30363D] overflow-hidden">
                    {result.disciplines.map((d, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm border-b border-[#21262D] last:border-0">
                        <span>{d.label}</span>
                        <span className="text-[#8B949E] font-mono-cad text-xs">{d.count} item(s)</span>
                        <span className="font-mono-cad">{fmt(d.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.earthwork && (
                <div className="rounded-lg border border-[#F43F5E]/40 bg-[#F43F5E]/5 px-3 py-2 flex items-center justify-between text-sm" data-testid="cost-earthwork">
                  <span className="text-[#F43F5E]">Excavation @ {result.earthwork.grade}% grade</span>
                  <span className="text-[#8B949E] font-mono-cad text-xs">cut {result.earthwork.cut_cy} cy · fill {result.earthwork.fill_cy} cy</span>
                  <span className="font-mono-cad text-[#F0F6FC]">{fmt(result.earthwork.cost)}</span>
                </div>
              )}
              {result.retaining_wall && (
                <div className="rounded-lg border border-[#F43F5E]/40 bg-[#F43F5E]/5 px-3 py-2 flex items-center justify-between text-sm" data-testid="cost-retaining">
                  <span className="text-[#F43F5E]">High-side retaining wall</span>
                  <span className="text-[#8B949E] font-mono-cad text-xs">{result.retaining_wall.height_ft} ft × {result.retaining_wall.length_ft} ft</span>
                  <span className="font-mono-cad text-[#F0F6FC]">{fmt(result.retaining_wall.cost)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm font-mono-cad text-[#8B949E] px-1">
                <span>Subtotal {fmt(result.subtotal)}</span>
                <span>Contingency (10%) {fmt(result.contingency)}</span>
              </div>
            </TabsContent>

            <TabsContent value="materials" className="space-y-4 pt-3">
              <div className="rounded-xl border border-[#30363D] bg-gradient-to-br from-[#0D1117] to-[#161B22] p-4 flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8B949E] font-mono-cad">Materials Total</div>
                  <div className="font-heading text-2xl font-bold text-[#38BDF8]" data-testid="materials-total">{fmt(result.materials_total)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="bom-csv-btn" className="border-[#30363D] bg-transparent hover:border-[#34D399] hover:text-[#34D399] h-8">
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadBomPdf} data-testid="bom-pdf-btn" className="border-[#30363D] bg-transparent hover:border-[#38BDF8] hover:text-[#38BDF8] h-8">
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </Button>
                </div>
              </div>
              {Object.entries(matByCat).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-xs uppercase tracking-widest text-[#8B949E] font-mono-cad mb-2">{cat}</div>
                  <div className="rounded-lg border border-[#30363D] overflow-hidden">
                    {items.map((m, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm border-b border-[#21262D] last:border-0" data-testid="material-row">
                        <span className="truncate max-w-[45%]">{m.item}</span>
                        <span className="text-[#8B949E] font-mono-cad text-xs">{m.qty.toLocaleString()} {m.unit} × {fmt(m.unit_cost)}</span>
                        <span className="font-mono-cad text-[#F0F6FC]">{fmt(m.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-[#6E7681] px-1">Quantities are an automatic takeoff estimate from the plan geometry — confirm with a licensed estimator before ordering.</p>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
